import { FC, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { AdvancedViewStep } from '../questionnaire-wizard/advanced-view-step';
import { useFormikContext } from 'formik';
import {
  QuestionnaireResponseProvider,
  useQuestionnaireResponse,
} from '../questionnaire-wizard';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, ExpandLess, ExpandMore } from '@mui/icons-material';
import { CutsheetPdfGenerator } from '../shared/cutsheet-pdf-generator';
import {
  InputSelect,
  InputText,
  InputTextArea,
} from '@farmshare/ui-components';
import type {
  CatalogSpecification,
  CutsheetCatalog,
  CutsheetQuestionnaire,
  DefaultSelections,
  PricingOverride,
  Specification,
  SpecificationOption,
} from '../../types';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Display labels for each supported pricing model. */
const PRICING_MODEL_LABELS: Record<string, string> = {
  per_item: 'Per Item',
  per_pound: 'Per Pound',
  per_hanging_weight: 'Per Hanging Weight',
  fixed_price: 'Fixed Price',
};

const PRICING_MODELS = Object.entries(PRICING_MODEL_LABELS).map(
  ([value, label]) => ({ value, label }),
);

// ─── Types ────────────────────────────────────────────────────────────────────

/** A catalog item ready for use in grouped Autocomplete options. */
interface CatalogItemOption {
  id: string;
  title: string;
  /** Display group label */
  sectionLabel: 'Cuts' | 'Trim' | 'Extras';
  /** Raw type for DefaultSelection.itemType */
  itemType: 'cut' | 'trim' | 'extra';
  defaultPricing?: PricingOverride;
  /** Specifications embedded directly on this catalog item */
  specifications?: CatalogSpecification[];
}

export interface QuestionnaireSettingsFormProps {
  /** Catalog data used to populate override item lists and default-selection options. */
  capabilities: CutsheetCatalog;
  /** The questionnaire being edited — used to initialise the default selections preview. */
  questionnaire: CutsheetQuestionnaire;
  /**
   * The underlying CutsheetCapability (with vendor, species, printSettings).
   * Used by the Print Settings tab to render the live PDF preview and diff
   * against the base capability settings.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  capability?: any;
}

// ─── Helper: simple tab panel ─────────────────────────────────────────────────

function TabPanel({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: number;
  index: number;
}) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      sx={{ pt: 2 }}
    >
      {value === index && children}
    </Box>
  );
}

// ─── Sub-component: Pricing Override Fields ───────────────────────────────────

interface PricingFieldsProps {
  override: PricingOverride;
  onChange: (updated: PricingOverride) => void;
}

/**
 * Inline pricing fields shared by catalog-item and spec-option override cards.
 * @param root0
 * @param root0.override
 * @param root0.onChange
 */
const PricingFields: FC<PricingFieldsProps> = ({ override, onChange }) => (
  <Grid container spacing={1.5}>
    {/* Pricing model */}
    <Grid size={{ xs: 12, sm: 6 }}>
      <Select
        size="small"
        fullWidth
        value={override.model}
        onChange={(e) =>
          onChange({
            ...override,
            model: e.target.value as PricingOverride['model'],
          })
        }
      >
        {PRICING_MODELS.map((m) => (
          <MenuItem key={m.value} value={m.value}>
            {m.label}
          </MenuItem>
        ))}
      </Select>
    </Grid>

    {/* Base price */}
    <Grid size={{ xs: 6, sm: 3 }}>
      <TextField
        size="small"
        label="Price ($)"
        type="number"
        fullWidth
        value={override.price}
        onChange={(e) =>
          onChange({ ...override, price: parseFloat(e.target.value) || 0 })
        }
        inputProps={{ min: 0, step: 0.01 }}
      />
    </Grid>

    {/* Optional minimum charge */}
    <Grid size={{ xs: 6, sm: 3 }}>
      <TextField
        size="small"
        label="Min Charge ($)"
        type="number"
        fullWidth
        value={override.minimumCharge ?? ''}
        onChange={(e) => {
          const val = e.target.value ? parseFloat(e.target.value) : undefined;
          onChange({ ...override, minimumCharge: val });
        }}
        inputProps={{ min: 0, step: 0.01 }}
      />
    </Grid>

    {/* Optional human-readable description */}
    <Grid size={{ xs: 12 }}>
      <TextField
        size="small"
        label="Description (optional)"
        fullWidth
        value={override.description ?? ''}
        onChange={(e) =>
          onChange({
            ...override,
            description: e.target.value || undefined,
          })
        }
      />
    </Grid>
  </Grid>
);

// ─── Sub-component: Pricing Override Card ─────────────────────────────────────

interface PricingOverrideCardProps {
  item: CatalogItemOption;
  override: PricingOverride;
  onChange: (updated: PricingOverride) => void;
  onDelete: () => void;
}

/**
 * Inline editor card for a single catalog-item pricing override.
 * @param root0
 * @param root0.item
 * @param root0.override
 * @param root0.onChange
 * @param root0.onDelete
 */
const PricingOverrideCard: FC<PricingOverrideCardProps> = ({
  item,
  override,
  onChange,
  onDelete,
}) => (
  <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={item.sectionLabel} size="small" variant="outlined" />
          <Typography variant="body2" fontWeight="medium">
            {item.title}
          </Typography>
        </Stack>
        <Tooltip title="Remove override">
          <IconButton size="small" onClick={onDelete} color="error">
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <PricingFields override={override} onChange={onChange} />
    </CardContent>
  </Card>
);

// ─── Sub-component: Spec-option override section ──────────────────────────────

interface SpecOptionPricingSectionProps {
  spec: Specification;
  overrides: Record<string, PricingOverride>;
  onChange: (updated: Record<string, PricingOverride>) => void;
}

/**
 * Collapsible card that shows all options for a single select/multiselect spec.
 * Each option can have an independent pricing override toggled on/off.
 * @param root0
 * @param root0.spec
 * @param root0.overrides
 * @param root0.onChange
 */
const SpecOptionPricingSection: FC<SpecOptionPricingSectionProps> = ({
  spec,
  overrides,
  onChange,
}) => {
  const [expanded, setExpanded] = useState(false);

  const options: SpecificationOption[] = (
    Array.isArray(spec.options) ? spec.options : []
  ).map((o) =>
    typeof o === 'string' ? { label: o, value: o } : (o as SpecificationOption),
  );

  if (options.length === 0) return null;

  const activeCount = Object.keys(overrides).length;

  const setOptionOverride = (optionValue: string, pricing: PricingOverride) =>
    onChange({ ...overrides, [optionValue]: pricing });

  const clearOptionOverride = (optionValue: string) => {
    const next = { ...overrides };
    delete next[optionValue];
    onChange(next);
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Header — click to expand/collapse */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          onClick={() => setExpanded((v) => !v)}
          sx={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <Stack spacing={0.25}>
            <Typography variant="body2" fontWeight="medium">
              {spec.title}
            </Typography>
            {spec.description && (
              <Typography variant="caption" color="text.secondary">
                {spec.description}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {activeCount > 0 && (
              <Chip
                size="small"
                label={`${activeCount} override${activeCount > 1 ? 's' : ''}`}
                color="primary"
                variant="outlined"
              />
            )}
            {expanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </Stack>
        </Stack>

        {expanded && (
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            {options.map((opt) => {
              const optValue = opt.value ?? opt.label ?? '';
              const isOverridden = !!overrides[optValue];
              const currentOverride = overrides[optValue];

              return (
                <Box key={optValue}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: isOverridden ? 1 : 0 }}
                  >
                    <Typography variant="body2">{opt.label}</Typography>
                    {isOverridden ? (
                      <Tooltip title="Remove override">
                        <Chip
                          size="small"
                          label="Override set"
                          color="primary"
                          variant="outlined"
                          onDelete={() => clearOptionOverride(optValue)}
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        size="small"
                        label="Set override"
                        variant="outlined"
                        onClick={() =>
                          setOptionOverride(optValue, {
                            model: opt.pricing?.model ?? 'per_item',
                            price: opt.pricing?.price ?? 0,
                            minimumCharge: opt.pricing?.minimumCharge,
                            description: opt.pricing?.description,
                          })
                        }
                        sx={{ cursor: 'pointer' }}
                      />
                    )}
                  </Stack>
                  {isOverridden && (
                    <PricingFields
                      override={currentOverride}
                      onChange={(updated) =>
                        setOptionOverride(optValue, updated)
                      }
                    />
                  )}
                  <Divider sx={{ mt: 1 }} />
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

// DefaultItemCard replaced by AdvancedViewStep interactive preview — see tab 2.

// ─── Sub-component: Default Selections Syncer ─────────────────────────────────

/**
 * Bridges QuestionnaireResponseProvider state back to Formik after user
 * interactions in the Default Selections tab.
 * Must be mounted inside both a QuestionnaireResponseProvider and a Formik form.
 * @returns null — side-effect only component
 */
const DefaultSelectionsSyncer: FC = () => {
  const { selectedCuts, trimSelections, specifications, primalNotes } =
    useQuestionnaireResponse();
  const { setFieldValue } = useFormikContext();

  useEffect(() => {
    setFieldValue('defaultSelections', {
      selectedCuts,
      trimSelections,
      specifications: specifications.length > 0 ? specifications : undefined,
      primalNotes:
        Object.keys(primalNotes).length > 0 ? primalNotes : undefined,
    } satisfies DefaultSelections);
    // setFieldValue is stable; the other deps are context state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCuts, trimSelections, specifications, primalNotes]);

  return null;
};

// ─── Sub-component: Print Settings Tab Content ────────────────────────────────

interface PrintSettingsTabContentProps {
  capabilities: CutsheetCatalog;
  questionnaire: CutsheetQuestionnaire;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  capability?: any;
  values: {
    useCustomPrintSettings: boolean;
    printSettings: NonNullable<CutsheetQuestionnaire['printSettings']>;
    defaultSelections?: DefaultSelections;
  };
  setFieldValue: (field: string, value: unknown) => void;
}

/**
 * Builds the `formValues` shape expected by CutsheetPdfGenerator from a
 * CutsheetCatalog. Preserves `_id` on cuts and specifications so the
 * generator's visibility tree can reference them by ID.
 */
function catalogToFormValues(catalog: CutsheetCatalog) {
  const primalGroups = catalog.primals.map((primal, i) => ({
    name: '',
    order: i,
    primals: [
      {
        _id: primal.id,
        name: primal.title || '',
        order: i,
        cuts: catalog.cuts
          .filter((c) => c.groupId === primal.id)
          .map((c) => ({
            _id: c.id,
            name: c.printedText || c.title || '',
            order: c.sortOrder ?? 0,
            isDeleted: false,
          })),
        isDeleted: false,
      },
    ],
    isDeleted: false,
  }));

  return {
    primalGroups,
    specifications: catalog.specifications.map((s) => ({
      _id: s.id,
      name: s.printedText || s.title || '',
      value: '',
      notes: '',
    })),
    trim: catalog.trim.map((t) => ({
      name: t.printedText || t.title || '',
      weightInLb: 0,
    })),
    extras: catalog.extras.map((e) => ({
      name: e.printedText || e.title || '',
      weightInLb: 0,
    })),
  };
}

/**
 * Union of cut/spec IDs referenced by the questionnaire's questions and its
 * default selections. Drives the "Show only attached items" visibility filter.
 */
function computeAttachedIds(
  questionnaire: CutsheetQuestionnaire,
  defaultSelections?: DefaultSelections,
): { cutIds: string[]; specificationIds: string[] } {
  const cutIds = new Set<string>();
  const specIds = new Set<string>();

  for (const q of questionnaire.questions ?? []) {
    // free-selection
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const qAny: any = q;
    if (Array.isArray(qAny.availableCutIds)) {
      for (const id of qAny.availableCutIds) if (id) cutIds.add(String(id));
    }
    if (Array.isArray(qAny.options)) {
      for (const opt of qAny.options) {
        if (Array.isArray(opt?.cutIds)) {
          for (const id of opt.cutIds) if (id) cutIds.add(String(id));
        }
      }
    }
    if (qAny.specificationId) specIds.add(String(qAny.specificationId));
  }

  for (const sel of defaultSelections?.selectedCuts ?? []) {
    if (sel?.optionId) cutIds.add(String(sel.optionId));
  }
  for (const sel of defaultSelections?.specifications ?? []) {
    if (sel?.specification) specIds.add(String(sel.specification));
  }

  return {
    cutIds: Array.from(cutIds),
    specificationIds: Array.from(specIds),
  };
}

const PrintSettingsTabContent: FC<PrintSettingsTabContentProps> = ({
  capabilities,
  questionnaire,
  capability,
  values,
  setFieldValue,
}) => {
  const formValues = useMemo(
    () => catalogToFormValues(capabilities),
    [capabilities],
  );

  const attachedIds = useMemo(
    () => computeAttachedIds(questionnaire, values.defaultSelections),
    [questionnaire, values.defaultSelections],
  );

  // Pseudo-capability for the preview renderer (it reads vendor name and
  // species for the header). We don't pass printSettings here — the
  // generator is driven by the controlled props below.
  const previewCapability = useMemo(
    () => ({
      _id: capability?._id,
      vendor: capability?.vendor,
      animalSpecies: capability?.species,
    }),
    [capability],
  );

  return (
    <Stack spacing={2}>
      <FormControlLabel
        control={
          <Switch
            checked={values.useCustomPrintSettings}
            onChange={(_, checked) => {
              setFieldValue('useCustomPrintSettings', checked);
              // When enabling the override for the first time, seed the
              // questionnaire's printSettings from the capability so the user
              // starts from the inherited defaults instead of a blank slate.
              const hasExistingOverride =
                values.printSettings &&
                Object.keys(values.printSettings).length > 0;
              if (
                checked &&
                !hasExistingOverride &&
                capability?.printSettings
              ) {
                setFieldValue('printSettings', {
                  ...capability.printSettings,
                });
              }
            }}
          />
        }
        label="Use custom print settings for this questionnaire"
      />

      <Typography variant="body2" color="text.secondary">
        {values.useCustomPrintSettings
          ? 'This questionnaire will use the settings below instead of the capability defaults. Overridden settings are highlighted.'
          : 'This questionnaire inherits print settings from the capability. Enable the toggle above to customize.'}
      </Typography>

      {values.useCustomPrintSettings && (
        <CutsheetPdfGenerator
          formValues={formValues}
          capability={previewCapability}
          printSettings={values.printSettings}
          onPrintSettingsChange={(next) => setFieldValue('printSettings', next)}
          baseSettings={capability?.printSettings}
          restrictedIds={attachedIds}
          restrictedActionLabel="Hide items not in this questionnaire"
          hideSaveButton
          hideBlankDownload
        />
      )}
    </Stack>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * Three-tab form used in the Questionnaire Settings modal:
 *
 * 1. **General** — name, description, published state
 * 2. **Pricing Overrides** — per-item pricing overrides for this questionnaire
 * 3. **Default Selections** — pre-populated answers when customers open the form
 *
 * All tab values are read from / written to Formik context so the parent
 * `useModal` `save` call receives the complete form state on submit.
 * @param root0
 * @param root0.capabilities
 * @param root0.questionnaire
 */
export const QuestionnaireSettingsForm: FC<QuestionnaireSettingsFormProps> = ({
  capabilities,
  questionnaire,
  capability,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const { values, setFieldValue } = useFormikContext<{
    pricingOverrides: Record<string, PricingOverride>;
    specOptionPricingOverrides: Record<string, Record<string, PricingOverride>>;
    defaultSelections: DefaultSelections;
    useCustomPrintSettings: boolean;
    printSettings: NonNullable<CutsheetQuestionnaire['printSettings']>;
  }>();

  // ── Catalog item pricing overrides ────────────────────────────────────────

  const pricingOverrides: Record<string, PricingOverride> =
    values.pricingOverrides ?? {};

  const setOverride = (itemId: string, override: PricingOverride) => {
    setFieldValue('pricingOverrides', {
      ...pricingOverrides,
      [itemId]: override,
    });
  };

  const clearOverride = (itemId: string) => {
    const next = { ...pricingOverrides };
    delete next[itemId];
    setFieldValue('pricingOverrides', next);
  };

  // ── Spec option pricing overrides ─────────────────────────────────────────

  const specOptionPricingOverrides: Record<
    string,
    Record<string, PricingOverride>
  > = values.specOptionPricingOverrides ?? {};

  /**
   * Updates the per-option overrides for a single spec.
   * If `optionOverrides` is empty, removes the spec key entirely to keep the
   * stored value clean.
   * @param specId
   * @param optionOverrides
   */
  const setSpecOptionOverrides = (
    specId: string,
    optionOverrides: Record<string, PricingOverride>,
  ) => {
    const next = { ...specOptionPricingOverrides };
    if (Object.keys(optionOverrides).length === 0) {
      delete next[specId];
    } else {
      next[specId] = optionOverrides;
    }
    setFieldValue('specOptionPricingOverrides', next);
  };

  // ── Default selections (cut / trim / spec pre-selected state) ────────────
  // Managed entirely by QuestionnaireResponseProvider on tab 2.
  // The count here is used for the tab badge only.
  const defaultSelectionsCount =
    (values.defaultSelections?.selectedCuts?.length ?? 0) +
    (values.defaultSelections?.trimSelections?.trim?.length ?? 0);

  // ── Build flat catalog item list ───────────────────────────────────────────

  const allCatalogItems: CatalogItemOption[] = [
    ...capabilities.cuts.map((c) => ({
      id: c.id,
      title: c.title,
      sectionLabel: 'Cuts' as const,
      itemType: 'cut' as const,
      defaultPricing: c.pricing,
      specifications: c.specifications,
    })),
    ...capabilities.trim.map((t) => ({
      id: t.id,
      title: t.title,
      sectionLabel: 'Trim' as const,
      itemType: 'trim' as const,
      defaultPricing: t.pricing,
      specifications: t.specifications,
    })),
    ...capabilities.extras.map((e) => ({
      id: e.id,
      title: e.title,
      sectionLabel: 'Extras' as const,
      itemType: 'extra' as const,
      defaultPricing: e.pricing,
      specifications: e.specifications,
    })),
  ];

  /** Items that don't yet have an override — shown in the "Add" autocomplete. */
  const availableToOverride = allCatalogItems.filter(
    (item) => !pricingOverrides[item.id],
  );

  /** Items that already have an override — rendered as editable cards. */
  const overriddenItems = allCatalogItems.filter(
    (item) => !!pricingOverrides[item.id],
  );

  /** Specs with selectable options that can receive per-option pricing overrides. */
  const specsWithOptions = capabilities.specifications.filter(
    (s) =>
      (s.inputType === 'select' || s.inputType === 'multiselect') &&
      Array.isArray(s.options) &&
      s.options.length > 0,
  );

  /** Total count of active spec-option overrides (across all specs). */
  const totalSpecOptionOverrides = Object.values(
    specOptionPricingOverrides,
  ).reduce((sum, map) => sum + Object.keys(map).length, 0);

  /** Combined tab badge count for pricing overrides. */
  const totalPricingOverrides =
    overriddenItems.length + totalSpecOptionOverrides;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Tabs
        value={activeTab}
        onChange={(_, v: number) => setActiveTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          label="General"
          id="settings-tab-0"
          aria-controls="settings-tabpanel-0"
        />
        <Tab
          label={
            totalPricingOverrides > 0
              ? `Pricing Overrides (${totalPricingOverrides})`
              : 'Pricing Overrides'
          }
          id="settings-tab-1"
          aria-controls="settings-tabpanel-1"
        />
        <Tab
          label={
            defaultSelectionsCount
              ? `Default Selections (${defaultSelectionsCount})`
              : 'Default Selections'
          }
          id="settings-tab-2"
          aria-controls="settings-tabpanel-2"
        />
        <Tab
          label="Print Settings"
          id="settings-tab-3"
          aria-controls="settings-tabpanel-3"
        />
      </Tabs>

      {/* ── 0: General ────────────────────────────────────────────────────── */}
      <TabPanel value={activeTab} index={0}>
        <Stack spacing={3}>
          <InputText label="Questionnaire Name" nameOverride="name" required />
          <InputTextArea
            label="Description"
            nameOverride="description"
            rows={3}
            hint="Optional description shown to customers"
          />
          <InputSelect
            label="Status"
            nameOverride="status"
            options={[
              { value: 'draft', label: 'Draft' },
              { value: 'published', label: 'Published' },
              { value: 'archived', label: 'Archived' },
            ]}
            hint="Draft is hidden from customers. Archived forms are soft-deleted."
          />
        </Stack>
      </TabPanel>

      {/* ── 1: Pricing Overrides ──────────────────────────────────────────── */}
      <TabPanel value={activeTab} index={1}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Override the default pricing for specific items on this
            questionnaire. Items without an override use the pricing defined on
            the capability.
          </Typography>

          {/* Add new catalog-item override */}
          {availableToOverride.length > 0 && (
            <Autocomplete
              size="small"
              options={availableToOverride}
              groupBy={(item) => item.sectionLabel}
              getOptionLabel={(item) => item.title}
              value={null}
              onChange={(_: SyntheticEvent, item: CatalogItemOption | null) => {
                if (!item) return;
                // Seed the override with the item's existing pricing so the
                // user has a sensible starting point rather than zeroes.
                setOverride(item.id, {
                  model: item.defaultPricing?.model ?? 'per_pound',
                  price: item.defaultPricing?.price ?? 0,
                  minimumCharge: item.defaultPricing?.minimumCharge,
                  description: item.defaultPricing?.description,
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add item pricing override..."
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <Add
                        fontSize="small"
                        sx={{ mr: 0.5, color: 'text.secondary' }}
                      />
                    ),
                  }}
                />
              )}
            />
          )}

          {overriddenItems.length === 0 && availableToOverride.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ py: 2 }}
            >
              No catalog items available to override.
            </Typography>
          )}

          {overriddenItems.length === 0 && availableToOverride.length > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ py: 2 }}
            >
              No item overrides configured yet. Use the field above to add one.
            </Typography>
          )}

          {/* Existing catalog-item overrides */}
          {overriddenItems.map((item) => (
            <PricingOverrideCard
              key={item.id}
              item={item}
              override={pricingOverrides[item.id]}
              onChange={(updated) => setOverride(item.id, updated)}
              onDelete={() => clearOverride(item.id)}
            />
          ))}

          {/* Spec option pricing overrides */}
          {specsWithOptions.length > 0 && (
            <>
              <Divider />
              <Typography variant="subtitle2" color="text.secondary">
                Specification Option Pricing
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Set additional charges for specific specification choices (e.g.,
                a particular steak thickness incurs an upcharge).
              </Typography>
              {specsWithOptions.map((spec) => (
                <SpecOptionPricingSection
                  key={spec.id}
                  spec={spec}
                  overrides={specOptionPricingOverrides[spec.id] ?? {}}
                  onChange={(optionOverrides) =>
                    setSpecOptionOverrides(spec.id, optionOverrides)
                  }
                />
              ))}
            </>
          )}
        </Stack>
      </TabPanel>

      {/* ── 2: Default Selections ─────────────────────────────────────────── */}
      <TabPanel value={activeTab} index={2}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Interact with the cutsheet preview below to pre-select cuts and trim
          that will be checked by default when a customer opens this
          questionnaire. Customers can still change any of these values.
        </Typography>
        <QuestionnaireResponseProvider
          questionnaire={{
            ...questionnaire,
            defaultSelections: values.defaultSelections,
          }}
          catalog={capabilities}
          onClose={() => {}}
          fixedCutsheetType="vendor"
        >
          {/* Syncs provider state back to Formik on every change */}
          <DefaultSelectionsSyncer />
          <AdvancedViewStep disableNonQuestionnaireCuts={false} />
        </QuestionnaireResponseProvider>
      </TabPanel>

      {/* ── 3: Print Settings ────────────────────────────────────────────── */}
      <TabPanel value={activeTab} index={3}>
        <PrintSettingsTabContent
          capabilities={capabilities}
          questionnaire={questionnaire}
          capability={capability}
          values={values}
          setFieldValue={setFieldValue}
        />
      </TabPanel>
    </>
  );
};
